import { ComplianceCertificate, ComplianceChecklist } from "./compliance";
export * from "./compliance";

export interface res {
  resCode: number | string;
  resMessage: string;
  message: object | object[];
}

export interface ApiResponse<T> {
  message?: T;
  success?: boolean;
  data?: T;
  resCode?: string | number;
  resMessage?: string;
  status?: number | number;
  user?: {
    id: string;
    username: string;
  };
  roleData?: rolesObjType;
  permissionSeed?: SeedModule[];
}

export interface ApiRes<T> {
  success: boolean;
  data?: T;
  message?: T;
  resCode: string | number;
  resMessage: string;
  status: number | number;
  error?: any;
  total?: number;
  limit?: number;
  skip?: number;
  results?: T extends Array<infer U> ? U[] : never;
  score?: number; // For test submission responses
}

export interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}
export interface User {
  id: string;
  tenant_name: string;
  username: string;
  email?: string;
  roles: string[]; // e.g., ["orgadmin"] or ["superadmin"]
  mustResetPassword?: boolean; // For Org Admins with temporary passwords
  roleId?: string;
  tenantId?: string;
  tenant_id?: string;
  isDeleted?: boolean;
  employeeId?: string;
  org_name?: string;
  org_admin_name?: string;
  org_admin_phone?: string;
}
// user level
export interface OrgUserProfile {
  id: string;
  tenant_name: string;
  firstName: string;
  lastName: string;
  email?: string;
  mobile: string;
  department: string;
  employeeId?: string;
  supervisor?: string;
  role: string;
  region?: string[];
  premise?: string[];
  password: string;
  roles: string[]; // e.g., ["orgadmin"] or ["superadmin"]
}
// admin level
export interface OrgAdminProfile {
  id: string;
  tenant_name: string;
  org_admin_name: string;
  password: string;
  username?: string;
  roles: string[]; // e.g., ["orgadmin"] or ["superadmin"]
}

export interface SuperadminLoginResponse {
  message: string;
  token: string;
  admin: User;
}

export interface OrgAdminLoginResponse {
  message: string;
  token: string;
  user: User;
}

export type LoginResponse = SuperadminLoginResponse | OrgAdminLoginResponse;

export interface VerifyAuthResponse {
  isAuthenticated: boolean;
}

//  --------- except apis

export interface UserObject {
  _id: string;
  createdAt: string;
  updatedAt: string;
  department: string | { $oid: string }; // Can be { $oid: string }, string, or null
  departmentName?: string; // Optional, added if backend includes it
  email: string;
  employeeId: string;
  firstName: string;
  isDeleted: boolean;
  lastName: string;
  mobile: string;
  password: string;
  role: string | { $oid: string }; // Allow role as string or object with $oid
  roles?: string[]; // Added as optional to match API response for users 3 and 4
  status: string;
  supervisor: string;
  tenantId?: string;
  premise?: string | Array<{ $oid: string }> | string[];
  region?: string | Array<{ $oid: string }> | string[] | any;
}

export interface OrgUserDirectoryItem {
  _id: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  department?: string | { $oid: string };
  role?: string | { $oid: string };
  roles?: string[];
  employeeId?: string;
  mobile?: string;
  premise?: string | Array<{ $oid: string }> | string[];
  region?: string | Array<{ $oid: string }> | string[] | any;
  status?: string;
  supervisor?: string;
  isDeleted?: boolean;
}

// -------premises
export interface premisesApiResponse {
  _id?: string;
  id: string;
  premisesName: string;
  addressLine1: string;
  townCity: string;
  postcode: string;
  region: string;
  updatedAt: number;
  isStatus: boolean;
  basement?: boolean;
  floor?: number;
}
export interface RowData {
  id: string;
  premisesName: string;
  addressLine1: string;
  city: string;
  region: string;
  lastUpdate: string;
  status: string;
  action: null;
}

//---------Task
export interface Task {
  responsiblePersonList?: string;
  id: string;
  task: string;
  topic: string;
  reference: string;
  frequency: string;
  premises?: string;
  weeklyInterval?: string;
  monthlyInterval?: string;
  premisesId?: string;
  fromDueDate: string;
  // toDueDate: string;
  module?: string;
  furtherDetails: string;
  isRecurring: boolean;
  nextExecutionDate?: string;
  daysRemaining?: string; // Made optional since backend calculates it
  displayDate?: string;
  status?: string;
  tenant_name?: string;
  createdDate?: string;
  updatedAt?: string;
  referenceId?: string;
  folderId?: string;
}

export interface TaskResponse {
  success: boolean;
  task?: Task;
  updatedTask?: Task;
  message?: string;
}

export interface TasksResponse {
  success: boolean;
  tasks: Task[];
}

//--Regions
export type regionObjectType = {
  id: string;
  _id: string;
  regionName: string;
  regionCode: string;
  tenantId: string;
  updatedAt: number;
};

export type regionRowType = {
  id: string;
  regionName: string;
  regionCode: string;
  premisesCount: number;
  tenantId: string;
  updatedAt: number;
  lastUpdate: string;
  action: null;
};

export interface BudgetCategory {
  id: string;
  categoryId: string;
  categoryName: string;
}

export type budgetCategoryObjType = BudgetCategory & {
  action?: any;
};

export interface Department {
  id: string;
  _id?: string;
  departmentName: string;
  departmentId: string;
}

export type departmentObjType = Department & {
  action?: any;
};

export type AccessMap = Record<string, boolean>;

// export type RolePermissions = Record<string, Set<string>>;
// export type seedInterface = Record<
//   string,
//   { actions: string[]; adminOnly?: string[]; readonly?: string[] }
// >;

export interface SubModule {
  key: string;
  name: string;
  actions: string[];
  adminOnly?: string[];
  readonly?: string[];
}

export interface SeedModule {
  key: string;
  name: string;
  subModules: SubModule[];
}

export type RolePermissions = Record<string, Set<string>>;

// export type SeedType = Module[];

// export type RolePermissions = {
//   [subModuleKey: string]: Set<string>;
// };

export interface Roles {
  _id?: string;
  roleName: string;
  // Workflow tier (independent of CRUD permissions) used e.g. by Washroom
  // Management to decide who is assignable maintenance staff vs. admin.
  staffCategory?: "housekeeping" | "maintenance" | "admin" | "";
}

export type rolesObjType = Roles & {
  permissions?: Record<string, string[]> | RolePermissions;
};

export type OrgUserType = {
  _id: string | { $oid: string };
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  premise?: string | Array<{ $oid: string }> | string[] | { $oid: string }[];
};
// Staff Training and Evaluation
export interface Training {
  _id?: string;
  id?: string;
  topic: string;
  category: string;
  moduleType?: string;
  date: string;
  duration: string;
  /** 24-hour "HH:MM" — set alongside endTime; duration is derived from the pair. */
  startTime?: string;
  /** 24-hour "HH:MM" — set alongside startTime; duration is derived from the pair. */
  endTime?: string;
  instructor: {
    trainerId: string;
    trainerEmployeeId: string;
    trainerName: string;
  }[];
  method?: string;
  files: string[];
  trainees: Trainee[];
  status: "Active" | "Inactive";
  tenantId: string;
  tenant_name?: string;
  createdAt: string;
  updatedAt: string;
  createTest?: "Yes" | "No";
  tests?: Test[];
  testsData?: any[];
  /** Premise ids this training applies to. Empty/undefined means all premises. */
  premises?: string[];
  /** Region ids this training applies to. Empty/undefined means all regions. */
  regions?: string[];
  // Canonical derived fields computed server-side (TrainingController.js's
  // computeTrainingDerivedFields) so screens no longer re-derive these
  // independently. Present on responses from `all` and `getTrainingById`.
  /** Training date is today-or-later (UTC). */
  isUpcoming?: boolean;
  /** Training date is before today (UTC). */
  isPast?: boolean;
  isCurrent?: boolean;
  /** Which test category a UI should currently surface for this training. */
  displayedTestCategory?: "Pre-Test" | "Post-Test";
  /** True only when every trainee has a non-null preTestScore. */
  hasPreTestCompleted?: boolean;
  /** True only when every trainee has a non-null postTestScore. */
  hasPostTestCompleted?: boolean;
  preTestCompletionCount?: number;
  postTestCompletionCount?: number;
  totalTrainees?: number;
}

export interface Test {
  id: string;
  trainingId: string;
  topic: string;
  testCategory: "Pre-Test" | "Post-Test";
  date: string;
  duration: string;
  quiz: {
    id: number;
    question: string;
    options: { text: string; isCorrect: boolean }[];
  }[];
  status: string;
  results?: {
    traineeId: string;
    score: number;
    submittedAt: string;
    answers: { questionId: number; selectedOption: number }[];
  }[];
  createdAt: string;
  updatedAt: string;
  tenantId: string;
}
export interface Trainee {
  traineeId: string;
  traineeEmployeeId: string;
  name: string;
  email?: string; // Optional, added for clarity
  department: string;
  role: string | { $oid: string };
  roleName?: string; // Optional, added for clarity
  attendance: {
    "Pre-Test": "Present" | "Absent";
    "Post-Test": "Present" | "Absent";
  };
  results?: {
    preTestId: string | null;
    preTestScore: number | null;
    postTestId: string | null;
    postTestScore: number | null;
  };
  premise?: string; // Added for filtering trainees by premises
}

export interface QuizQuestion {
  id: number;
  question: string;
  options: { text: string; isCorrect: boolean }[];
}

export type FireDrill = {
  id?: string;
  moduleType?: string;
  region: string;
  premise: string;
  premisesId?: string;
  report_send_email?: string[];
  date_of_test_drills: string | null;
  person_responsibility: string;
  closing_timeline: string | null;
  date_of_evidence: string | null;
  participation_rate: string | number;
  observation_identified_for_improvement: string;
  area: string;
  actions: string;
  floor: string;
  users?: string[];
  closure_evidence: string;
  status?: string;
  average_drill: string | number;
  evacuation_trend: string | number;
  next_drill_scheduled: string | null;
  evidence_closure_files: File[] | string[] | null;
  feedback_gap_analysis?: string;
};

// Risk Assesment

export interface initialActRiskType {
  ProbabilityOfFailure: number;
  SeverityOfOutCome: number;
  AbilityToDetectFailure: number;
  TotalRiskScore: number;
}
export interface calcRiskType {
  SeverityOfOutCome: number;
  AbilityToDetectFailure: number;
  SolutionImplemented: string;
  nextStepOnSustenance: string;
}
export interface hospitalRiskType {
  _id?: string;
  process: string;
  subProcesses: string;
  objective: string;
  responsibilityIndividual: string;
  possibleFailureRisk: string;
  actionsSolution: string;
  TimeLineToImplement: number;
  initialActualRisk: initialActRiskType;
  recalculationOfRisk: calcRiskType;
}
export interface areaRiskType {
  _id?: string;
  assessment?: string;
  process: string;
  subProcesses: string;
  objective: string;
  possibleFailureRisk: string;
  initialActualRisk: initialActRiskType;
  actionsSolution: string;
  responsibilityIndividual: string;
  TimeLineToImplement: number;
  monitoringProcedure: string;
  remarks: string;
  recalculationOfRisk: {
    SolutionImplemented: string;
    nextStepOnSustenance: string;
    SeverityOfOutCome: number;
    AbilityToDetectFailure: number;
  };
}

export interface initialPremisesObject {
  id?: string;
  premisesName: string;
  addressLine1: string;
  responsiblePerson: string;
}
export interface initialRiskAssObject {
  _id?: string;
  premises: string[];
  responsiblePerson: string[];
  assessor: string[];
  assessorEmailAddress: string[];
  addressLine1: string;
  ImageOfPremises?: string;
  suggestedDate: string; // will be remove
  signature: string; // will be removed
  status: "open" | "closed" | "submitted";
}

export const assessmentInitalKeys: (keyof initialRiskAssObject)[] = [
  "_id",
  "premises",
  "responsiblePerson",
  "assessor",
  "assessorEmailAddress",
  "addressLine1",
  "ImageOfPremises",
  "suggestedDate",
  "signature",
];
export interface riskobjectType {
  id?: string;
  premises: string[];
  responsiblePerson: string[];
  assessor: string[];
  assessorEmailAddress: string[];
  addressLine1: string;
  ImageOfPremises: string;
  // HospitalWideRisk: hospitalRiskType[];
  // AreaSpecificRisk: areaRiskType[];
  suggestedDate: string;
  signature: string;
}

// ------------- Task -----------

export interface ITaskObject {
  responsibility: string;
  task: string;
  topic: string;
  reference: string;
  fromDueDate: string;
  toDueDate: string;
  furtherDetails: string;
}

// ------------- Fire safety equipment

export interface initialEquipment {
  id?: string;
  equipmentInventory: string | undefined;
}
export interface initialChecks {
  _id?: string;
  checkType: string;
  frequency: string;
  nextScheduled: string;
  createdAt?: string;
}

export interface allChecksType {
  checkType: string;
  frequency: string;
  nextScheduled: string;
}
export interface AssetFile {
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface AssetsState {
  // info
  _id?: string;
  equipmentInventory?: string;
  equipmentInventoryName?: string;

  equipmentSubCategory: string;
  premisesName: string;
  premisesId: string;
  region: string;
  name: string;
  image: string;
  vendorName: string;
  mobileNumber: string;
  vendorEmail: string;
  // inventory
  inventory: string;
  unitNumber: string;
  manufacture: string;
  type: string;
  installedOn: string;
  totalNumber: string;
  uploadReferenceDocument: string;
  floor: string;
  areaName: string;
  equipmentManufactureDate?: string;
  equipmentExpireDate?: string;
  // assets details (optional, equipment-type specific)
  cylinderNumber?: string;
  panelNo?: string;
  loopNo?: string;
  deviceNo?: string;
  loopType?: string;
  capacity?: string;
  location?: string;
  hptTestDate?: string;
  lastRefillDate?: string;
  description?: string;
  Qty?: string | number;
  files: AssetFile[];
  checksAssets: initialChecks[];
  nextInspectionDate?: string;
  inspectionStatus?: string;
  HB_No?: string;
}
export interface CurrentAssets {
  _id?: string;
  premisesName: "";
  premisesId: "";
  equipmentInventory: "";
  equipmentInventoryName: "";
  equipmentSubCategory: "";

  region: "";
  name: "";
  image: "";
  vendorName: "";
  vendorEmail: "";
  mobileNumber: "";
  // inventory
  inventory: "";
  unitNumber: "";
  manufacture: "";
  type: "";
  installedOn: "";
  totalNumber: "";
  uploadReferenceDocument: "";
  floor: "";
  areaName: "";
  equipmentManufactureDate: "";
  equipmentExpireDate: "";
  nextInspectionDate: "";
  inspectionStatus: "";
  files: [];
  HB_No?: string;
  checksAssets: initialChecks[];
  assets: AssetsState;
}
export interface maintenance {
  id?: string;
  internalExternal: string;
  Checked: string;
  lastDateOfCheck: string;
  checkCompletedBy: string;
  observation: string;
  actionTaken: string;
  recheckDate: string;
  recheckBy: string;
  image?: string;
  files: AssetFile[];
  equipment: string[];
}
export interface MaintenanceArr {
  _id?: string;
  internalExternal: string;
  Checked: string;
  lastDateOfCheck: string;
  checkCompletedBy: string;
  observation: string;
  actionTaken: string;
  recheckDate: string;
  recheckBy: string;
}

export interface MaintenanceTask {
  id?: string;
  taskId: string;
  equipmentName: string;
  maintenanceType: "";
  scheduledDate: string;
  assignedTo: string;
  completionDate?: string;
  checkList: string | null;
  status: "Scheduled" | "In Progress" | "Completed" | "Overdue";
  remarks?: string;
  attachments?: Attachment[];
  region?: string;
  hospitalName?: string;
  frequency?:
    | "Daily"
    | "Weekly"
    | "Monthly"
    | "Quarterly"
    | "Semi-Annual"
    | "Annual"
    | "One-time"
    | "";
  isVendorTask?: boolean;
  vendorName?: string;
  vendorContact?: string;
  nextMaintenanceDate?: string;
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PolicyTags {
  id: string;
  policytagId: string;
  policytagName: string;
}

export type policyTagsObjType = PolicyTags & {
  action?: any;
};

export interface LegalContracttypes {
  id: string;
  legalContractId: string;
  legalContractName: string;
}

export type legalContracttypesObjType = LegalContracttypes & {
  action?: any;
};

export interface FireRiskProcesses {
  id: string;
  fireRiskProcessId: string;
  fireRiskProcessName: string;
}

export type fireRiskProcessObjType = FireRiskProcesses & {
  action?: any;
};

export interface FireRiskSubProcesses {
  id: string;
  fireRiskProcessId: string;
  fireRiskSubProcessName: string;
}

export type fireRiskSubProcessObjType = FireRiskSubProcesses & {
  action?: any;
};

export interface ComplianceTypeListType {
  _id?: string;
  complianceTypeName: string;
  renewalPeriod: string;
}

export interface Equipment {
  _id?: string;
  equipmentId: string;
  equipmentName: string;
  manufactureDate?: string;
  expireDate?: string;
}
export type EquipmentObjType = Equipment & {
  action?: any;
};

export interface FetchDashboardArgs {
  region?: string;
  premise?: string;
  startDate?: string;
  endDate?: string;
}

export interface DashboardDataType {
  summary: {
    premises: number;
    assets: {
      total: number;
      failed: number;
    };
    recentAlerts: number;
    tasks: number;
  };
  metrics: {
    incidentsReported: number;
    feedbackReceived: number;
    auditFindings: number;
    complianceStatus: string;
  };
  mapData: {
    locations: Array<{
      id?: string;
      _id?: string;
      name: string;
      region: string;
      address: string;
      addressLine1: string;
      regionName: string;
      latitude: number;
      longitude: number;
    }>;
  };
  teamActivity: {
    recentActivities: Array<{
      type: string;
      action: string;
      item: string;
      date: string;
      user: string;
    }>;
  };
}

// -------fire safety audits
export interface CreateCheckListForm {
  id?: string;
  moduleName: string;
  checkList: string;
  itemNo?: number;
  riskRating?: "Low" | "Medium" | "High";
  appliesToArea: string;
  auditType: string;
  checkListDescription: string;
  isActive: boolean;
}
export interface CheckListItem {
  _id: string;
  checkList: string;
  itemNo?: number;
  riskRating?: "Low" | "Medium" | "High";
  appliesToArea: string;
  auditType: string;
  moduleName: string;
  isActive: boolean;
}

export interface Question {
  id: string;
  text: string;
  moduleName: string;
  isEditing?: boolean;
}

export interface Question {
  id: string;
  text: string;
  moduleName: string;
  isEditing?: boolean;
}

export interface ExternalAgency {
  id?: string;
  _id?: string;
  agencyName: string;
  category: string;
  contactPerson: string;
  contactNumber: string;
  email: string;
  status: "Active" | "Inactive";
  mouContractName?: string;
  mouContractExpiryDate?: string;
  tenantId?: string;
  createdBy?: string | { $oid: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface Section {
  id: string;
  title: string;
  moduleName: string;
  questions: Question[];
}
export interface Audit {
  moduleName: string;
  assignedAuditors: string;
  _id: string;
  auditId: string;
  startDate: string;
  premise: string;
  department: string[];
  auditorName: string;
  audit_status?: "Scheduled" | "In Progress" | "Completed";
}

export interface PerformAuditData {
  auditId: string;
  questions: {
    [key: string]: {
      question: string;
      isSelected: string;
      risk: string;
      comments: string;
    };
  };
  actionReport: ActionReport;
  tenantId?: string;
}

export interface AuditFormData {
  _id?: string;
  premise: string;
  auditFrequency: string;
  startDate: string;
  reminder: string;
  checklist: string[];
  assignedAuditors: string;
  department: string[];
  auditType: string;
  schedule: string;
  moduleName: string;
  audit_status?: "Scheduled" | "In Progress" | "Completed";
}
export interface CreateAuditProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (auditData: AuditFormData) => void;
  isEdit?: boolean;
  isView?: boolean;
  initialData?: AuditFormData;
  moduleName: string;
}

// Fire Safety Audits - PerformCheckList interfaces
export interface ChecklistItem {
  _id: string;
  appliesToArea: string;
  auditType: string;
  checkList: string;
  checkListDescription: string;
  createdAt: string;
  isActive: boolean;
  itemNo: number;
  riskRating: string;
  sections: Section[];
  tenant_name: string;
}

export interface ActionReport {
  // moduleName: string;
  observation: string;
  uploadFiles: Array<{
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
  actionPlan: string;
  severity: string;
  assignedTo: string;
  assignedAuditors: string;

  targetCompletionDate: string;
  actualCompletionDate: string;
  status: string;
  closureEvidence: Array<{
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
  remarks: string;
  signature: string;
}

// Laws and Regulations Audit interfaces
export interface LawsAndRegulationsAudit {
  _id?: string;
  id?: string;
  lawId: string;
  lawName: string;
  agencyName: string;
  agencyEmail: string;
  periodicity:
    | "quarterly"
    | "half yearly"
    | "annually"
    | "not specific"
    | "unannounced";
  lastVisitDate: string;
  reportFile: Array<{
    url: string;
    name: string;
    uploadedAt: string;
  }>;
  closureDate: string;
  closureFile: Array<{
    url: string;
    name: string;
    uploadedAt: string;
  }>;
  pendingItems: string;
  status: "Pending" | "Completed" | "Overdue";
  reminderSet: boolean;
  lastUpdated: string;
  premisesId?: string;
  tenant_name: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AuditReminder {
  visitId: string;
  lawName: string;
  agencyName: string;
  agencyEmail: string;
  closureDate: string;
  daysUntilClosure: number;
  severity: "High" | "Critical";
  type: "Closure Approaching" | "Closure Overdue";
  message: string;
}

export interface CreateAuditFormData {
  lawId: string;
  lawName: string;
  agencyName: string;
  agencyEmail: string;
  periodicity:
    | "quarterly"
    | "half yearly"
    | "annually"
    | "not specific"
    | "unannounced";
  lastVisitDate: string;
  closureDate: string;
  pendingItems?: string;
  status?: "Pending" | "Completed" | "Overdue";
  reminderSet?: boolean;
  premisesId?: string;
}

export interface UpdateAuditFormData extends Partial<CreateAuditFormData> {
  id: string;
}

// Laws and Regulations Task Types
export interface LawsAndRegulationsTask {
  _id?: string;
  id?: string;
  lawId: string;
  lawName: string;
  taskType: "Policy" | "Audit";
  taskTitle: string;
  description: string;
  frequency:
    | "One Time"
    | "Daily"
    | "Weekly"
    | "Monthly"
    | "Quarterly"
    | "Annually";
  dueDate: string;
  reminderDate: string;
  priority: "Low" | "Medium" | "High" | "Critical";
  status: "Pending" | "In Progress" | "Completed" | "Overdue";
  assignedTo: {
    name: string;
    email: string;
  };
  agencyName?: string;
  agencyEmail?: string;
  attachments?: Array<{
    url: string;
    name: string;
    uploadedAt: string;
  }>;
  remarks: string;
  reminderSet: boolean;
  lastUpdated: string;
  daysRemaining?: string;
  premisesId: string;
  tenantId: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface CreateTaskFormData {
  lawId: string;
  lawName: string;
  taskType: "Policy" | "Audit";
  taskTitle: string;
  description: string;
  frequency:
    | "One Time"
    | "Daily"
    | "Weekly"
    | "Monthly"
    | "Quarterly"
    | "Annually";
  dueDate: string;
  reminderDate: string;
  priority: "Low" | "Medium" | "High" | "Critical";
  status?: "Pending" | "In Progress" | "Completed" | "Overdue";
  assignedTo: {
    name: string;
    email: string;
  };
  agencyName?: string;
  agencyEmail?: string;
  attachments?: Array<{
    url: string;
    name: string;
    uploadedAt: string;
  }>;
  remarks?: string;
  reminderSet?: boolean;
  premisesId: string;
}

export interface UpdateTaskFormData extends Partial<CreateTaskFormData> {
  id: string;
}

// Staff of Independent Units types
export interface StaffIdentityDocument {
  name: string;
  url: string;
  uploadedAt: string;
  key?: string;
}

export interface StaffOfIndependentUnits {
  _id?: string;
  id?: string;
  name: string;
  designation: string;
  phone: string;
  email: string;
  entity: string;
  region: string;
  premise: string;
  status: "Active" | "Inactive";
  lastUpdated: string;
  tenantId: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  identityDocuments?: StaffIdentityDocument[];
}

export interface CreateStaffFormData {
  name: string;
  designation: string;
  phone: string;
  email: string;
  entity: string;
  premise: string;
  region: string;
  status?: "Active" | "Inactive";
  tenantId?: string;
  createdAt?: string;
  createdBy?: string;
  identityDocuments?: StaffIdentityDocument[];
}

export interface UpdateStaffFormData extends Partial<CreateStaffFormData> {
  id: string;
}

export interface IndependentUnit {
  id: string;
  name: string;
  type?: string;
  description?: string;
}

export interface PremiseWithIndependentUnits {
  _id: string;
  premisesName: string;
  region: string;
  independentUnits?: IndependentUnit[];
}

export interface EquipmentManagementTask {
  _id?: string;
  taskName: string;
  equipmentId: string;
  equipmentName: string;
  remediationAction: string;
  serialNumber: string;
  warrantyExpiryDate: string;
  status: string;
  responsiblePerson: string;
  tenantId: string;
  taskType: string;
  actionDate: string;
  notes: string;
}

export interface EquipmentInventory {
  _id: string;
  equipmentCategoryId: string;
  equipment: string;
  equipmentName: string;
  category: string;
  subCategory: string;
  serialNumber: string;
  manufacturer: string;
  supplier: string;
  purchaseDate: string;
  warrantyExpiryDate: string;
  amcStatus: string;
  department: string;
  condition: string;
  premises: string;
  region: string;
  attachments: Array<{
    url: string;
    name: string;
    uploadedAt: string;
  }>;
}

export interface EquipmentCategoryType {
  _id?: string;
  categoryName: string;
  categoryDescription: string;
}

export interface EquipmentSubcategoryType {
  _id?: string;
  subcategoryName: string;
  subcategoryDescription: string;
  categoryId: string;
}

export interface EmergencyCategoryType {
  _id?: string;
  categoryName: string;
  categoryDescription: string;
}

export interface EmergencySubcategoryType {
  _id?: string;
  subcategoryName: string;
  subcategoryDescription: string;
  categoryId: string;
}

export interface ManufacturerListType {
  _id?: string;
  manufacturerName: string;
  contactPerson: string;
  phone: string;
  email?: string;
  address?: string;
  websiteUrl?: string;
  notes?: string;
}

export interface maintenanceTypeListType {
  _id?: string;
  maintenanceTypeName: string;
  description?: string;
  frequency?: string;
  typicalDuration?: string;
  responsibleDepartment?: string;
  priorityLevel?: string;
}

export interface LocationType {
  _id?: string;
  regionId: string;
  premiseId: string;
  department: string;
  room: string;
  floorNumber: string;
  zone?: string;
  locationCode?: string;
  description?: string;
}

export interface UnitsofMeasureType {
  _id?: string;
  unitName: string;
  unitSymbol: string;
  unitType: string;
  description?: string;
}

export interface IncidentSeverityLevelsType {
  _id?: string;
  incidentSeverityLevelName: string;
  description?: string;
  recommendedAction?: string;
}

export interface HazardMaterialCategoryType {
  _id?: string;
  hazardMaterialCategoryName: string;
  description?: string;
  hazardClassification?: string;
  safetyPrecautions?: string;
}

export interface DisposalMethodsListType {
  _id?: string;
  methodName: string;
  description?: string;
  regulatoryBody?: string;
  safetyPrecautions?: string;
}

// supplier

export type supplierFormData = {
  _id?: string;
  id?: string;
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
};

export const emptyForm: supplierFormData = {
  name: "",
  phoneNumber: "",
  email: "",
  address: "",
};

export interface HazardousMaterialType {
  _id?: string;
  materialName: string;
  description?: string;
  quantity: string;
  storageLocation?: string;
  materialCategory?: string;
  unitOfMeasure?: string;
  casNumber?: string;
  batchNumber?: string;
  supplier?: string;
  expiryDate?: string;
  safetyDataSheetName?: string;
  safetyDataSheet?: File | null;
}

export interface StorageHandlingType {
  _id?: string;
  materialName: string;
  storageLocation?: string;
  storageConditions?: string;
  containerType?: string;
  // storageCapacity?: string;
  // currentQuantity: string;
  // storageDate?: string;
  // handlingProcedures?: string;
  ppeRequired: string[];
  // spillResponseProcedures?: string;
  // hazardLabelsPresent: boolean;
  // sdsAvailable: boolean;
  lastSafetyInspectionDate?: string;
  inspectedBy?: string;
  // incidentLogs?: string;
  remarks?: string;
  temperature?: string;
  humidity?: string;
}

export interface SafetyComplianceType {
  _id?: string;
  complianceDocumentId?: string;
  regulationName?: string;
  certificationBody?: string;
  complianceStatus?: string;
  certificationExpiryDate?: string; // ISO date string
  lastInspectionDate?: string;
  nextInspectionDate?: string;
  inspectorName?: string;
  auditFindingsSummary?: string;
  correctiveActionsRequired?: boolean;
  correctiveActionDetails?: string;
  equipmentSafetyChecks?: string;
  safetyTrainingCompliance?: string;
  emergencyPreparednessMeasures?: string;
  infectionControlAdherence?: string;
  staffCertificationsVerified?: boolean;
  highestQualificationRecorded?: string;
  ongoingTraining?: string;
  numberOfSafetyIncidents?: string; // Or number if you prefer
  incidentDescriptions?: string;
  followUpActions?: string;
  remarks?: string;
  attachments?: any[];
}

export interface TrackingMovementType {
  _id?: string;
  materialName: string;
  movementId?: string;
  fromLocation?: string;
  toLocation?: string;
  quantityMoved: string;
  unitOfMeasure?: string;
  movementDateTime?: string; // ISO string
  approvedBy?: string;
  remarks?: string;
}

export interface ChecklistItemType {
  description: string;
  status: "Pass" | "Fail" | "Attention";
  comment?: string;
}

export interface InspectionComplianceType {
  _id?: string;
  inspectionId?: string;
  inspectionSchedule?: string;
  nextInspectionDate?: string;
  previousInspectionDate?: string;
  scheduledInspector?: string;
  inspectionDate?: string;
  location?: string;
  inspectionType?: string;
  complianceStatus?: string;
  checklist?: ChecklistItemType[];
  issuesFound?: boolean;
  issueDescriptions?: string;
  correctiveActionsRequired?: boolean;
  correctiveActionPlan?: string;
  correctiveActionDeadline?: string;
  followUpInspectionDate?: string;
  followUpStatus?: string;
  verificationComments?: string;
  supportingDocuments?: string;
  inspectorApproval?: string;
  finalRemarks?: string;
}

export interface DisposalEventType {
  _id?: string;
  disposalId?: string;
  disposalDateTime?: string;
  materialName: string;
  materialId?: string;
  quantityDisposed: string;
  unitOfMeasure?: string;
  sourceLocation?: string;
  reasonForDisposal?: string;
  conditionBeforeDisposal?: string;
  disposalMethod?: string;
  disposalVendor?: string;
  transportMethod?: string;
  authorizedBy?: string;
  complianceReference?: string;
  attachment?: string;
  witnessedBy?: string;
  safetyPrecautions?: string;
  incidentDuringDisposal?: boolean;
  incidentDetails?: string;
  remarks?: string;
  attachments?: any[];
}

// Hazardous Waste Daily Tracking
export interface HazardousWasteLogType {
  _id?: string;
  wasteCategory: string; // Infectious, Sharps, Pathological and anatomical, etc.
  wasteType?: string;
  generatedFromLocation: string;
  approxQuantity: string;
  quantityUnit: string;
  wasteDate: string; // YYYY-MM-DD
  notes?: string;
  checklistFormat?: string;
  attachments?: Attachment[];
}

export interface SearchFiltersType {
  reportType: "expiry" | "incident" | "movement" | "compliance";
  casNo?: string;
  materialType?: string;
  materialName?: string;
  storageLocation?: string;
  expiryBefore?: string;
  expiryAfter?: string;
  incidentFrom?: string;
  incidentTo?: string;
  incidentSeverity?: string;
  incidentType?: string;
  month?: string;
  year?: string;
  fromLocation?: string;
  toLocation?: string;
  movedBy?: string;
  receivedBy?: string;
  quantityMin?: string;
  quantityMax?: string;
  checklistTemplate?: string;
  inspectionFrom?: string;
  inspectionTo?: string;
  complianceStatus?: string;
  department?: string;
  inspectorName?: string;
  issueFound?: string;
  movementType?: string;
}

export interface ReportResultType {
  [key: string]: any; // Generic flexible object for result columns
}

export interface IncidentSpillReportType {
  _id?: string;
  incidentId?: string;
  dateTime: string;
  location: string;
  materialsInvolved: string[]; // material IDs
  severity: "Minor" | "Moderate" | "Major" | "Critical";
  description: string;
  actionsTaken: string;
  reportedBy: string; // user ID
  attachments?: File[];
}

export interface InventoryMaterial {
  id: string;
  name: string;
}

export interface TenantUserSummary {
  id: string;
  name: string;
}

export interface CreateTenantData {
  tenant_name: string;
  org_name: string;
  org_admin_name: string;
  org_admin_email: string;
  org_admin_phone: string;
  db_hostname?: string;
  db_port?: number;
}

export interface TenantResponse {
  success: boolean;
  message: string;
  data: {
    // Client-safe fields only — the backend never returns db_* credentials.
    id: string;
    tenant_name: string;
    database_name: string;
    org_name: string;
    org_admin_name: string;
    org_admin_email: string;
    org_admin_phone: string;
    status: string;
    createdOn: string;
    lastUpdatedOn: string;
    is_temp_password: boolean;
  };
}

export interface WashroomTypeListType {
  id?: string;
  _id?: string;
  typeId: string;
  typeName: string;
}

export interface WashroomTypeListResponse extends WashroomTypeListType {
  _id: string;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
}
// ========== Utility Systems Endpoints ==========
// Asset Inventory Tracking
export interface AssetInventory {
  _id?: string;
  id?: string;
  assetId?: string;
  name: string;
  type: string;
  location: string;
  purchaseDate: string;
  InstallationDate: string;
  manufacturer: string;
  model: string;
  warrantyExpiry: string;
  maintenanceVendor: string;
  status: string;
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
}
export interface ContractorVendorManagement {
  _id?: string;
  venderId?: string;
  vendorName: string;
  phoneNumber: string;
  email: string;
  timelines: string;
  compliance: string;
  attachments: Attachment[];
}

// master data
export interface UtilityAssetTypeListType {
  id?: string;
  _id?: string;
  typeId: string;
  typeName: string;
}

export interface SpaceUtilization {
  _id?: string;
  id?: string;
  premiseId: string;
  areaId?: string;
  projectId?: string;
  projectName?: string;
  utilizationType: "before" | "after";
  areaName: string;
  areaSize: number;
  unit?: "sqft" | "sqm";
  utilizationPercentage?: number;
  purpose?: string;
  department?: string;
  floor?: string;
  capacity?: number;
  currentOccupancy?: number;
  notes?: string;
  recordedDate: string;
  recordedBy?: string;
  documents?: Attachment[];
  isActive?: boolean;
  tenantId?: string;
  tenant_name?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface MaintenanceSchedule {
  id?: string;
  _id?: string;
  assetId: string;
  assetName?: string;
  frequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
  nextDueDate: string;
  technicianId: string;
  technicianName?: string;
  status: "Active" | "Inactive" | "Suspended";
  description?: string;
  priority?: "Low" | "Medium" | "High" | "Critical";
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PreventiveMaintenanceTask {
  id?: string;
  _id?: string;
  scheduleId?: string;
  assetId: string;
  assetName?: string;
  dueDate: string;
  assignedTo: string;
  assignedToName?: string;
  status: "Pending" | "In-Progress" | "Completed" | "Cancelled" | "Overdue";
  completedAt?: string;
  remarks?: string;
  priority?: "Low" | "Medium" | "High" | "Critical";
  isPredictive?: boolean;
  predictiveReason?: string;
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface MaintenanceLog {
  id?: string;
  _id?: string;
  assetId: string;
  assetName?: string;
  taskId?: string;
  technicianId: string;
  technicianName?: string;
  workDone: string;
  partsUsedJson?: Array<{
    partName: string;
    quantity: number;
    unit?: string;
    cost?: number;
  }>;
  timeTaken?: number;
  timeTakenUnit?: "minutes" | "hours";
  cost?: number;
  attachments?: Attachment[];
  remarks?: string;
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  createdAt?: string;
}

// ========== MONITORING & IOT INTEGRATION TYPES ==========

export interface IoTReading {
  id?: string;
  _id?: string;
  assetId: string;
  assetName?: string;
  locationId?: string;
  locationName?: string;
  sensorId: string;
  sensorName?: string;
  sensorType:
    | "energy"
    | "water_flow"
    | "temperature"
    | "humidity"
    | "pressure"
    | "other";
  value: number;
  unit: string;
  timestamp: string;
  gatewayId?: string;
  gatewayName?: string;
  status?: "normal" | "warning" | "critical" | "offline";
  metadata?: Record<string, any>;
  tenantId?: string;
  tenant_name?: string;
  createdAt?: string;
}

export interface IoTThreshold {
  id?: string;
  _id?: string;
  assetId?: string;
  assetName?: string;
  locationId?: string;
  locationName?: string;
  sensorId?: string;
  sensorName?: string;
  sensorType:
    | "energy"
    | "water_flow"
    | "temperature"
    | "humidity"
    | "pressure"
    | "other";
  minValue?: number;
  maxValue?: number;
  warningMin?: number;
  warningMax?: number;
  unit: string;
  alertEnabled: boolean;
  alertSeverity?: "low" | "medium" | "high" | "critical";
  notificationEmails?: string[];
  status: "active" | "inactive";
  description?: string;
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IoTAlert {
  id?: string;
  _id?: string;
  readingId?: string;
  thresholdId?: string;
  assetId: string;
  assetName?: string;
  sensorId: string;
  sensorName?: string;
  sensorType:
    | "energy"
    | "water_flow"
    | "temperature"
    | "humidity"
    | "pressure"
    | "other";
  alertType: "threshold_breach" | "warning" | "critical" | "offline" | "other";
  severity: "low" | "medium" | "high" | "critical";
  value: number;
  thresholdValue?: number;
  unit: string;
  message: string;
  description?: string;
  status: "active" | "acknowledged" | "resolved" | "dismissed";
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  triggeredAt: string;
  tenantId?: string;
  tenant_name?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ========== PROJECT PLANNING & APPROVALS TYPES ==========

export interface ProjectApprovalLevel {
  level: number;
  roleId: string;
  roleName: string;
  userId: string;
  userName: string;
  status: "Pending" | "Approved" | "Rejected";
  approverUserId?: string;
  approverName?: string;
  approvedAt?: string;
  remarks?: string;
}

export interface Project {
  id?: string;
  _id?: string;
  name: string;
  type: "Construction" | "Renovation";
  locationId: string;
  locationName?: string;
  startDate: string;
  endDate: string;
  projectLeadId: string;
  projectLeadName?: string;
  estimatedCost: number;
  status:
    | "Draft"
    | "Pending Approval"
    | "Approved"
    | "Rejected"
    | "In-Progress"
    | "Completed";
  description?: string;
  actualCost?: number;
  completionDate?: string;
  approvalLevels?: ProjectApprovalLevel[]; // form submission
  approvals?: ProjectApproval; // api response
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectApproval {
  id?: string;
  _id?: string;
  projectId: string;
  approvalLevels: ProjectApprovalLevel[];
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectPhase {
  id?: string;
  _id?: string;
  projectId: string;
  projectName?: string;
  name: string;
  estimatedCost: number;
  actualCost: number;
  status?: "Not Started" | "On Track" | "Over Budget" | "Under Budget";
  threshold?: number;
  alertSent?: boolean;
  tenantId?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChecklistItem {
  item: string;
  checked: boolean;
  remarks?: string;
}

export interface InfectionControlChecklistItem {
  item: string;
  checked: boolean;
  remarks?: string;
}

export interface InfectionControlChecklist {
  id?: string;
  _id?: string;
  projectId: string;
  locationId: string;
  locationName?: string;
  checklistItems: InfectionControlChecklistItem[];
  isApproved: boolean;
  approverId?: string;
  approverName?: string;
  approvedAt?: string;
  remarks?: string;
  isPatientSensitiveArea?: boolean;
  tenantId?: string;
  tenant_name?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}
// ========== SAFETY & FACILITY ROUNDS TYPES ==========

export interface roundPlanningType {
  id?: string;
  _id?: string;
  roundName: string;
  roundType: string;
  Frequency: string;
  Premise: string;
  status: string;
  Area: string[];
  assignedRole: string;
  checklistTemplate: string;
  sla: string;
  escalationMatrix?: string;
  tenantId?: string;
}

export interface EquipmentManagementType {
  _id?: string;
  equipmentName: string;
  equipmentDescription: string;
}
export interface RoundType {
  id?: string;
  _id?: string;
  roundType: string;
  createdAt?: string;
  updatedAt?: string;
}
